import { useTranslation } from 'react-i18next'
import { COLORS, Icon } from '@wehunt/ui'

export interface ReplayStatusScreenProps {
  title: string
  body?: string
  onClose: () => void
  onRetry?: () => void
}

/**
 * The full-bleed loading / empty / error surface, shown in place of the viewer.
 *
 * It occupies the same fixed frame the viewer does, so arriving on a share link
 * that has no replay still reads as a destination rather than as a broken page
 * behind the app shell.
 */
export function ReplayStatusScreen({ title, body, onClose, onRetry }: ReplayStatusScreenProps) {
  const { t } = useTranslation('replay')

  return (
    <section
      data-testid="replay-status-screen"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        background: COLORS.surface,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <header
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          padding: '10px 14px',
          background: COLORS.surfaceContainerHigh,
          borderBottom: `1px solid ${COLORS.outlineVariant}`,
        }}
      >
        <button
          type="button"
          aria-label={t('controls.close')}
          onClick={onClose}
          style={{
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            color: COLORS.onSurfaceVariant,
            display: 'inline-flex',
          }}
        >
          <Icon name="close" size={24} />
        </button>
      </header>

      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 10,
          padding: 24,
          textAlign: 'center',
        }}
      >
        <h1 style={{ margin: 0, fontFamily: COLORS.fontDisplay, fontSize: 20, fontWeight: 500 }}>
          {title}
        </h1>
        {body !== undefined && (
          <p style={{ margin: 0, fontSize: 14, color: COLORS.onSurfaceVariant, maxWidth: 380 }}>
            {body}
          </p>
        )}
        {onRetry !== undefined && (
          <button type="button" className="wh-btn wh-btn-primary" onClick={onRetry} style={{ marginTop: 6 }}>
            {t('status.retry')}
          </button>
        )}
      </div>
    </section>
  )
}
